import type { TFunction } from 'i18next'

import type { ComplianceState } from '../brokers/api'
import { paperName } from '../documents/names'
import type { ComplianceReasons } from './api'

/**
 * What a fleet row needs in order to say where it stands. A driver, a truck and a
 * trailer each answer to three things at once: their own paperwork, their carrier's
 * paperwork, and whether either of them is still someone we work with.
 */
export interface FleetStanding {
  complianceState: ComplianceState
  blockedByCarrier: boolean
  active: boolean
  availableForDispatch: boolean
}

/**
 * The carrier's half of the answer, as the family read sends it — a whole
 * `CarrierDetail` is one, which is how every screen passes it.
 */
export interface CarrierStanding {
  active: boolean
  complianceState: ComplianceState
  complianceReasons: ComplianceReasons | null
}

/**
 * One reason a record cannot be dispatched: its i18n key, and — when the reason is its
 * carrier's paperwork — what the carrier's evaluation found, so the sentence can say it.
 */
export interface UnavailableReason {
  key: string
  found?: ComplianceReasons
}

/**
 * Every reason this record cannot be dispatched, most specific first. Empty when it can.
 *
 * <p>`availableForDispatch` is the server's conclusion and the only thing trusted to
 * decide IF; these reasons only explain WHY. Recomputing the verdict here would put the
 * cascade rule in two places, which is the one thing the server-side bool exists to
 * prevent — and the carrier's findings are the server's too, never worked out here.</p>
 *
 * <p>The carrier's activation is a PARAMETER rather than something inferred from the
 * record: a driver whose own papers are incomplete under a carrier that was stood down
 * is unavailable twice over, and those two facts are indistinguishable from the child's
 * own fields. Inferring would have hidden the carrier exactly when the child also had a
 * problem — the case where "fix the CDL and he can go" is wrong.</p>
 */
export function unavailableBecause(record: FleetStanding, carrier: CarrierStanding): UnavailableReason[] {
  if (record.availableForDispatch) {
    return []
  }

  const reasons: UnavailableReason[] = []

  if (!record.active) {
    reasons.push({ key: 'compliance.standDownOwn' })
  }

  if (!carrier.active) {
    reasons.push({ key: 'compliance.carrierStoodDown' })
  }

  // Two independent tests, never a choice between them. `blockedByCarrier` reports the
  // source that WON the composition, and when a child and its carrier are equally
  // behind — both Pending, which is the day-one state of every new record — the
  // carrier's half disappears from the answer. Somebody files the driver's CDL, he
  // stays unavailable, and nothing had ever mentioned the carrier.
  //
  // And the word comes from the carrier's own state, never from the flag. The flag says
  // WHO holds the record back, not how hard: the server raises it for a carrier that is
  // merely Pending as much as for a Blocked one. Read as "blocked", it put "Blocked by
  // its carrier" beside every unit whose own papers were in order under a carrier still
  // short of its paperwork (Sergio's road test on the preview, 2026-09-22, TRKC-89) — and
  // "short of its paperwork" alone sent him looking for a paper, when what the carrier
  // lacked was an amount. So the sentence carries what the evaluation found.
  if (record.blockedByCarrier || carrier.complianceState !== 'Active') {
    reasons.push({
      key: carrier.complianceState === 'Blocked' ? 'compliance.blockedByCarrier' : 'compliance.carrierPaperwork',
      found: carrier.complianceReasons ?? undefined,
    })
  }

  if (record.complianceState !== 'Active') {
    reasons.push({ key: 'compliance.ownPaperwork' })
  }

  // Belt and braces: the server answered "not available" and nothing above accounted
  // for it. Better a vague true sentence than a row that looks perfectly fine.
  return reasons.length > 0 ? reasons : [{ key: 'compliance.unavailable' }]
}

/**
 * The order the findings are said in: what stopped the carrier outright, then what it
 * still owes, then what is merely waiting on a date.
 */
const FINDINGS = [
  ['expired', 'compliance.found.expired'],
  ['missing', 'compliance.found.missing'],
  ['belowMinimum', 'compliance.found.belowMinimum'],
  ['notYetEffective', 'compliance.found.notYetEffective'],
] as const

/**
 * A reason as the screen says it: its sentence, then what the carrier's evaluation
 * found — "its carrier's paperwork is outstanding — under the minimum: General
 * liability, Auto liability, Cargo". Nothing is added when nothing was found or the
 * server could not say, and the bare sentence is still true.
 */
export function describeReason(reason: UnavailableReason, t: TFunction): string {
  const sentence = t(reason.key)
  const found = reason.found

  if (found === undefined) {
    return sentence
  }

  const details = FINDINGS.flatMap(([kind, key]) =>
    found[kind].length === 0
      ? []
      : [t(key, { list: found[kind].map((type) => paperName(type, t)).join(', ') })],
  )

  return details.length === 0 ? sentence : `${sentence} — ${details.join('; ')}`
}
